import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import { useLiveDashboard } from '../hooks/useLiveDashboard';
import { useRBAC } from '../hooks/useRBAC';
import { useFusion } from '../contexts/FeatureFlagsContext';
import { fmtMoneyAtLeast as fmtMoney } from '../utils/moneyFormat';
import Card from './Card';
import { chainStatus, chainVerdict } from '../utils/attackPathChain';
import { stageFigure } from '../utils/attackPathStage';

const SEVERITY_BADGE = {
  CRITICAL: { bg: '#fed7d7', fg: '#c53030' },
  HIGH: { bg: '#feebc8', fg: '#c05621' },
  MODERATE: { bg: '#fefcbf', fg: '#975a16' },
  LOW: { bg: '#c6f6d5', fg: '#22543d' },
  INSUFFICIENT_DATA: { bg: '#e2e8f0', fg: '#4a5568' },
};

// Cool hues only (blue/aqua/violet) so a path's identity color never gets mistaken for a severity
// badge, which uses the warm reds/oranges/yellows above. Keyed by path, not array index: paths with
// no open finding are left out, so an index would shift a path's color when another path drops out.
const PATH_COLORS = {
  'credential-cloud-exfil': '#2a78d6',
  'edge-lateral-ransomware': '#1baf7a',
  'publicapp-cloud-databreach': '#4a3aa7',
};
const pathColor = key => PATH_COLORS[key] || '#2a78d6';

// Paths that match a Fusion consequence category get a link into the chain list filtered to it.
// publicapp-cloud-databreach has none: Fusion's DataExposure is AI-agent data leaks (rule R4), not
// the public-app breach narrative.
const FUSION_CONSEQUENCE_BY_PATH = {
  'credential-cloud-exfil': 'AccountTakeover',
  'edge-lateral-ransomware': 'DeviceFoothold',
};
const MUTED_RIBBON = '#94a3b8';

function AttackPathsSkeleton() {
  return (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        <div className="w-4 h-4 rounded-full bg-gray-200 animate-pulse" />
        <div className="h-4 w-40 bg-gray-200 rounded animate-pulse" />
      </div>
      <div className="h-[300px] bg-gray-100 rounded animate-pulse flex flex-col items-center justify-center gap-2">
        <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
        <span className="text-xs text-gray-400">Loading attack paths…</span>
      </div>
    </Card>
  );
}

// Shown when the fetch has never succeeded — distinct from AttackPathsEmpty so a transient request
// failure never masquerades as "this org has no categorized risk data".
function AttackPathsError() {
  return (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-4 h-4 text-gray-300" />
        <span className="text-sm font-semibold text-gray-800">Top Attack Paths</span>
      </div>
      <div className="h-[300px] flex items-center justify-center text-center px-4">
        <p className="text-xs text-amber-600">
          Attack-path data couldn't be loaded. It will retry automatically on the next dashboard
          refresh.
        </p>
      </div>
    </Card>
  );
}

// Shown when no path has an open finding (paths with none are left out by the backend) — never a
// blank gap on the dashboard.
function AttackPathsEmpty() {
  return (
    <Card>
      <div className="flex items-center gap-2 mb-3">
        <Shield className="w-4 h-4 text-gray-300" />
        <span className="text-sm font-semibold text-gray-800">Top Attack Paths</span>
      </div>
      <div className="h-[300px] flex items-center justify-center text-center px-4">
        <p className="text-xs text-gray-400">
          No attack paths have open findings.
        </p>
      </div>
    </Card>
  );
}

// Picks the path to show details for by default: the one with the largest live dollar exposure, so
// the most attention-worthy path is what a viewer sees first rather than always Path 1.
function defaultPathKey(paths) {
  if (!paths?.length) return null;
  return [...paths].sort((a, b) => (b.totalFinancialExposure ?? 0) - (a.totalFinancialExposure ?? 0))[0].pathKey;
}

// Tall enough for the stage-chain line to wrap to 2 lines (line-clamp-2) instead of being
// single-line-truncated — the longest path (4 stages, e.g. "Credential Theft › MFA Bypass ›
// Cloud/SaaS Takeover › Data Exfiltration & Fraud") doesn't fit on one line at this card width, and
// truncating it mid-arrow read as an incomplete/broken path rather than just a long label.
const CARD_H = 92;
const CARD_GAP = 12;
const SVG_W = 200;

// Lays out the Sankey: a stacked source bar whose ribbon thicknesses are proportional to each
// path's live dollar exposure (falling back to open-risk count, then equal weight, so a brand-new
// org with $0 mapped still renders a legible split rather than 3 invisible slivers), flowing to
// fixed-height destination cards whose vertical centers match the HTML card column exactly (both
// share the same CARD_H/CARD_GAP constants) so the SVG ribbons and the HTML cards align pixel-for-pixel.
function buildLayout(paths) {
  const n = paths.length;
  const totalH = n * CARD_H + (n - 1) * CARD_GAP;

  let weights = paths.map(p => Math.max(Number(p.totalFinancialExposure) || 0, 0));
  if (weights.every(w => w === 0)) weights = paths.map(p => Math.max(p.totalOpenRiskCount || 0, 0));
  if (weights.every(w => w === 0)) weights = paths.map(() => 1);
  const totalW = weights.reduce((a, b) => a + b, 0) || 1;

  const MIN_T = 10;
  const MAX_STACK = Math.min(totalH * 0.6, 130);
  const available = Math.max(MAX_STACK - MIN_T * n, 0);
  const thickness = weights.map(w => MIN_T + (w / totalW) * available);

  const stackH = thickness.reduce((a, b) => a + b, 0) + (n - 1) * 4;
  const stackTop = (totalH - stackH) / 2;
  let cursor = stackTop;
  const sourceY = thickness.map(t => {
    const center = cursor + t / 2;
    cursor += t + 4;
    return center;
  });

  const destY = paths.map((_, i) => i * (CARD_H + CARD_GAP) + CARD_H / 2);

  return { totalH, thickness, sourceY, destY, stackTop, stackHeight: Math.max(stackH, 1) };
}

function ribbonPath(x0, y0, x1, y1, t) {
  const half = t / 2;
  const midX = (x0 + x1) / 2;
  return `M ${x0} ${y0 - half} C ${midX} ${y0 - half}, ${midX} ${y1 - half}, ${x1} ${y1 - half} ` +
    `L ${x1} ${y1 + half} C ${midX} ${y1 + half}, ${midX} ${y0 + half}, ${x0} ${y0 + half} Z`;
}

export default function AttackPathsCard() {
  const apiCall = useApi();
  const navigate = useNavigate();
  // Same gate as FusionRouteGuard and the nav tab, so the link never leads to a redirect. Both hooks
  // are called unconditionally (a short-circuit would skip one and break the hook order).
  const fusionOn = useFusion();
  const { isAssignmentScopedOnly } = useRBAC();
  const fusionLink = fusionOn && !isAssignmentScopedOnly();
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  // Same split as ExposureTrajectoryChart: `loaded` gates the one-time big skeleton on first paint;
  // `refreshing` flips on for every fetch after that (including background live-poll ticks) so a
  // small spinner confirms an update is happening without wiping the whole card back to a skeleton.
  const [refreshing, setRefreshing] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);
  // A failed fetch must never erase data that already loaded — a background live-poll blip would
  // otherwise flip the whole card to the "no risk data" empty state, which reads as the org's data
  // being gone. On failure the last successful payload is kept and a non-destructive warning shown.
  const [fetchFailed, setFetchFailed] = useState(false);

  const fetchPaths = useCallback(() => {
    setRefreshing(true);
    apiCall('GET', '/allRisks/attack-paths')
      .then(d => {
        setData(d ?? null);
        setFetchFailed(false);
        setLoaded(true);
        setSelectedKey(prev => (prev && d?.paths?.some(p => p.pathKey === prev)) ? prev : defaultPathKey(d?.paths));
      })
      .catch(() => { setFetchFailed(true); setLoaded(true); })
      .finally(() => setRefreshing(false));
  }, [apiCall]);

  useEffect(() => { fetchPaths(); }, [fetchPaths]);

  useLiveDashboard({ refresh: fetchPaths });

  if (!loaded) return <AttackPathsSkeleton />;
  if (!data && fetchFailed) return <AttackPathsError />;
  if (!data || !data.paths?.length) return <AttackPathsEmpty />;

  const paths = data.paths;
  const selected = paths.find(p => p.pathKey === selectedKey) || paths[0];
  // Deduplicated by the backend — NOT a sum of the 3 cards' own totals, which can share a risk that
  // matches more than one path's narrative and would double-count it if added together here.
  const totalExposure = data.totalMappedExposure ?? 0;
  // The org's full current exposure (same figure Exposure Trajectory's "Now" shows) — always >=
  // totalExposure, since most risks (anything that doesn't keyword-match one of these 3 narrow
  // narrative chains) never map to a path. Shown for context so "mapped" never reads as disagreeing
  // with the neighboring chart.
  const orgExposure = data.orgCurrentExposure ?? 0;
  const { totalH, thickness, sourceY, destY, stackTop, stackHeight } = buildLayout(paths);

  return (
    <Card>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-semibold text-gray-800">Top Attack Paths</span>
          {refreshing && <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" aria-label="Refreshing" />}
          <span className="text-xs text-gray-400">(live org risk data)</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500 text-right">
            <span className="font-semibold text-gray-800">{fmtMoney(totalExposure)}</span> mapped to these paths
            {orgExposure > 0 && (
              <>
                {' '}<span className="text-gray-400">of {fmtMoney(orgExposure)} org total</span>
              </>
            )}
          </span>
          <button
            onClick={() => navigate('/attack-paths')}
            className="text-xs text-sky-600 hover:underline font-semibold flex-shrink-0"
          >
            View →
          </button>
          {/* On-demand recompute: attack paths are always computed live from the org's current
              risk data (no server-side cache — see AllRisksController "attack-paths"), so
              regenerating is simply re-running the same fetch the live-poll uses, but at the
              moment the user asks instead of waiting for the next change-token tick. Styled and
              placed (top-right, filled) to match the AI Intelligence Narrative card's Regenerate. */}
          <button
            type="button"
            onClick={fetchPaths}
            disabled={refreshing}
            title="Recompute attack paths from the organization's current risk data"
            className="flex items-center gap-1.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-60 px-3 py-1.5 rounded-md transition-colors flex-shrink-0"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Regenerating…' : 'Regenerate'}
          </button>
        </div>
      </div>

      {fetchFailed && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-1.5 mb-3">
          Latest refresh failed — showing the last successfully loaded data.
        </p>
      )}

      <div className="flex items-stretch gap-2">
        <svg width={SVG_W} height={totalH} viewBox={`0 0 ${SVG_W} ${totalH}`} className="flex-shrink-0">
          <rect x="4" y={stackTop} width="7" height={stackHeight} rx="2" fill={MUTED_RIBBON} />
          {paths.map((p, i) => {
            const active = p.pathKey === selected.pathKey;
            const color = p.severity === 'INSUFFICIENT_DATA' ? MUTED_RIBBON : pathColor(p.pathKey);
            return (
              <path
                key={p.pathKey}
                d={ribbonPath(11, sourceY[i], SVG_W, destY[i], thickness[i])}
                fill={color}
                opacity={active ? 0.62 : 0.28}
                style={{ transition: 'opacity 150ms ease' }}
              />
            );
          })}
        </svg>

        <div className="flex flex-col gap-3 flex-1 min-w-0">
          {paths.map((p, i) => {
            const active = p.pathKey === selected.pathKey;
            const color = p.severity === 'INSUFFICIENT_DATA' ? '#64748b' : pathColor(p.pathKey);
            const badge = SEVERITY_BADGE[p.severity] || SEVERITY_BADGE.MODERATE;
            const stageNames = p.stages.map(s => s.stageName).join(' › ');
            const stageCounts = p.stages.map(s => `${s.openRiskCount}/${s.riskCount}`).join(' · ');

            const consequenceKey = FUSION_CONSEQUENCE_BY_PATH[p.pathKey];

            return (
              <div key={p.pathKey} style={{ height: CARD_H }} className="relative">
                <button
                  type="button"
                  onClick={() => setSelectedKey(p.pathKey)}
                  style={{ borderColor: active ? color : undefined }}
                  className={`h-full w-full text-left rounded-lg border px-3 py-2 transition-colors ${
                    active ? 'bg-gray-50' : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-gray-800 truncate">{p.pathShortLabel}</span>
                    {p.hasSufficientData ? (
                      <span
                        className="text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0"
                        style={{ backgroundColor: badge.bg, color: badge.fg }}
                      >
                        {p.severity}
                      </span>
                    ) : (
                      <span className="text-[9px] text-gray-400 flex-shrink-0">Limited data</span>
                    )}
                  </div>
                  <div className="text-xs mt-0.5">
                    <span className="font-bold" style={{ color }}>{fmtMoney(p.totalFinancialExposure)}</span>
                    <span className="text-gray-500"> &middot; {p.totalOpenRiskCount}/{p.totalRiskCount} open</span>
                    {(() => {
                      const c = chainStatus(p.stages);
                      return c.openCount > 0 && (
                        <span className={c.complete ? 'text-red-600 font-semibold' : 'text-gray-400'}>
                          {' '}&middot; {c.complete ? 'complete chain' : `${c.openCount} of ${c.total} stages`}
                        </span>
                      );
                    })()}
                  </div>
                  <div
                    className="text-[10px] text-gray-400 mt-0.5 leading-snug line-clamp-2"
                    title={`${stageNames} — open/total: ${stageCounts}`}
                  >
                    {stageNames}
                  </div>
                </button>
                {fusionLink && consequenceKey && (
                  <Link
                    to={`/Fusion?consequence=${consequenceKey}`}
                    className="absolute bottom-1.5 right-2 text-[10px] text-sky-600 hover:underline"
                  >
                    See connected chains in Fusion
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-gray-100">
        <p className="text-xs text-gray-600 font-medium leading-snug mb-2">{selected.pathLabel}</p>

        {(() => {
          const chain = chainStatus(selected.stages);
          const verdict = chainVerdict(chain);
          const color = pathColor(selected.pathKey);
          const holds = s => (s.openRiskCount || 0) > 0;
          return (
            <>
              {/* Chain strip: a filled node is a stage with an open finding; a dashed link is
                  where the chain breaks in the org's data. */}
              <div className="flex items-center mb-2 px-1" aria-label={verdict?.text || 'Attack path stages'}>
                {chain.ordered.map((s, i) => (
                  <React.Fragment key={s.stageName}>
                    {i > 0 && (
                      <div
                        className="flex-1 mx-1 border-t-2"
                        style={holds(s) && holds(chain.ordered[i - 1])
                          ? { borderColor: color }
                          : { borderColor: '#cbd5e1', borderTopStyle: 'dashed' }}
                      />
                    )}
                    <span
                      title={`${s.stageName}: ${holds(s) ? `${s.openRiskCount} open finding${s.openRiskCount === 1 ? '' : 's'}` : 'no open findings'}`}
                      className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                      style={holds(s)
                        ? { backgroundColor: color, color: '#fff' }
                        : { border: '1.5px dashed #94a3b8', color: '#94a3b8', backgroundColor: '#fff' }}
                    >
                      {s.stageOrder}
                    </span>
                  </React.Fragment>
                ))}
              </div>
              {verdict && (
                <p className={`text-[11px] leading-snug mb-2 ${verdict.tone === 'complete' ? 'text-red-700 font-medium' : 'text-gray-600'}`}>
                  {verdict.text}
                </p>
              )}
            </>
          );
        })()}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2">
          {selected.stages.map(s => {
            const f = stageFigure(s);
            const open = f.kind === 'open';
            return (
              <div
                key={s.stageName}
                className={`rounded border px-2 py-1.5 min-w-0 ${
                  open ? 'border-gray-100 bg-gray-50' : 'border-dashed border-gray-300 bg-white'
                }`}
              >
                <div className="text-[9px] font-semibold text-gray-500 uppercase tracking-wide leading-tight flex items-start gap-1" title={s.stageName}>
                  <span className="break-words">{s.stageName}</span>
                  {s.hasCriticalGap && <AlertTriangle className="w-2.5 h-2.5 text-red-500 flex-shrink-0" />}
                </div>
                <div className={`mt-0.5 ${open ? 'text-xs font-bold text-gray-800' : 'text-[11px] font-medium text-amber-600'}`}>
                  {f.status}
                </div>
                {f.amount && <div className="text-[10px] text-gray-500">{f.amount}</div>}
                {s.signal && (
                  <div className="text-[9px] text-gray-500 mt-0.5 leading-tight" title={s.signal.source}>
                    {s.signal.label}: <span className="font-semibold text-gray-700">{s.signal.value}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {selected.topFixes?.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">Top fixes (live)</p>
            <ul className="space-y-1">
              {selected.topFixes.map(f => (
                <li key={f.riskId} className="flex items-center justify-between gap-2 text-xs text-gray-600">
                  <span className="truncate">
                    <span className="text-gray-400">{f.stageImpacted}:</span> {f.riskTitle}
                  </span>
                  <span className="font-semibold text-gray-800 whitespace-nowrap">{fmtMoney(f.exposureReductionEstimate)}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Methodology disclaimer: stages are keyword-matched against free-text risk data (no fixed
            attack-chain taxonomy exists yet — see AttackPathAnalyzer), and each "Top fix" is a single
            highest-$/effort-hour open risk per stage, not the stage's biggest risk or a combined sum. */}
        <p className="text-[10px] text-gray-400 mt-2 pt-2 border-t border-gray-100 leading-snug">
          Stages are matched by keyword heuristic, not a fixed taxonomy — a risk's wording can place
          it in an unexpected stage. "Risk forming" marks a stage with no findings yet — it carries
          no dollar figure. Each "Top fix" is the single open risk with the highest dollar
          reduction per estimated remediation hour in that stage — not its largest risk, and not a
          sum across fixes.
        </p>
      </div>
    </Card>
  );
}
